import { useCallback, useMemo } from 'react';
import { withNamespaces } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import {
    Button,
    Card,
    CardBody,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
} from 'reactstrap';

import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import DateUtils from 'src/services/utils/DateUtils';
import {
    ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS,
    ROUTE_PATH_ADMIN_GUARD_FREIGHTS_LIST,
} from 'src/modules/3rd-party-management/constants';
import { getCountryFlag } from '../../constants';
import { CargoTypes, FreightStatuses } from '../../freights/constants';

const RoutePoint = ({
    point,
}) => (
    <span className="d-flex align-items-center gap-1">
        <img
            alt={point.country}
            className="country-flag"
            src={getCountryFlag(point.country)}
        />
        {`${point.city}, ${point.country}`}
    </span>
);

const RecentFreights = ({
    freights = [],
    t,
}) => {
    const history = useHistory();
    const dateUtils = useMemo(() => new DateUtils(), []);

    const openFreightDetails = useCallback((freightId) => {
        history.push(
            ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS.replace(':freightId', freightId)
        );
    }, [history]);

    const tableColumns = useMemo(() => [
        {
            dataField: 'reference',
            text: t('Freight ID'),
            style: {
                width: '140px',
            },
            formatter: (reference) => (
                <b>{reference}</b>
            ),
        },
        {
            dataField: 'origin',
            text: t('Route'),
            style: {
                width: '260px',
            },
            formatter: (origin, row) => (
                <div className="d-flex align-items-center gap-2">
                    <RoutePoint point={origin} />
                    <i className="ri-arrow-right-line text-muted" />
                    <RoutePoint point={row.destination} />
                </div>
            ),
        },
        {
            dataField: 'cargo',
            text: t('Cargo'),
            style: {
                width: '170px',
            },
            formatter: (cargo) => (
                <div className="d-flex align-items-center gap-2">
                    <i className={`${CargoTypes[cargo.type]} font-size-18 text-muted`} />

                    <div>
                        <span className="d-block">{t(cargo.type)}</span>
                        <small className="text-muted">
                            {`${new Intl.NumberFormat('de-DE').format(cargo.weight)} kg`}
                        </small>
                    </div>
                </div>
            ),
        },
        {
            dataField: 'createdAt',
            text: t('Created'),
            style: {
                width: '120px',
            },
            formatter: (createdAt) => (
                dateUtils.convertDateToDate(createdAt, INTERNATIONAL_DATE_FORMAT)
            ),
        },
        {
            dataField: 'status',
            text: t('Status'),
            style: {
                width: '150px',
            },
            formatter: (status) => {
                const statusInfos = FreightStatuses[status];

                return (
                    <span className={`badge p-2 ${statusInfos.className}`}>
                        <i className={`${statusInfos.icon} align-middle me-1`} />
                        {t(statusInfos.title)}
                    </span>
                );
            },
        },
        {
            dataField: 'bids',
            text: t('Bids'),
            style: {
                width: '70px',
            },
        },
        {
            dataField: 'actions',
            text: t('Actions'),
            style: {
                width: '70px',
            },
            formatter: (_cell, row) => (
                <UncontrolledDropdown>
                    <DropdownToggle
                        aria-label={t('Freight actions')}
                        className="border-0"
                        color="primary"
                        outline
                    >
                        <i className="ri-more-2-fill" />
                    </DropdownToggle>

                    <DropdownMenu className="dropdown-menu-end">
                        <DropdownItem onClick={() => openFreightDetails(row.id)}>
                            <i className="ri-eye-line me-2" />
                            {t('View Freight')}
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            ),
        },
    ], [
        dateUtils,
        openFreightDetails,
        t,
    ]);

    return (
        <Card className="h-100 mb-0">
            <CardBody>
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                    <div>
                        <h6 className="mb-1">{t('Recent Freights')}</h6>
                        <p className="text-muted font-size-12 mb-0">
                            {t('View and manage your latest freights.')}
                        </p>
                    </div>

                    <Button
                        className="p-0"
                        color="link"
                        onClick={() => history.push(ROUTE_PATH_ADMIN_GUARD_FREIGHTS_LIST)}
                        type="button"
                    >
                        {t('View all')}
                        <i className="ri-arrow-right-line align-middle ms-1" />
                    </Button>
                </div>

                <BootstrapTable
                    bordered={false}
                    classes="table tpdd-table"
                    columns={tableColumns}
                    data={freights}
                    headerWrapperClasses="thead-light"
                    keyField="id"
                    responsive
                    striped
                    wrapperClasses="table-responsive"
                />
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(
    RecentFreights
);
